import {useLocation, useNavigate} from "react-router-dom";
import {
    IoArrowBack,
    IoCompass,
    IoCompassOutline,
    IoBookmarks,
    IoBookmarksOutline,
    IoArrowBackCircleOutline, IoExtensionPuzzleSharp, IoExtensionPuzzleOutline
} from "react-icons/io5";
import {RiSearchFill, RiSearchLine} from "react-icons/ri";
import { getLocalStorage } from "../../../utils/localStorage";
import { FROM_TATAV_PRACTICE } from "../../../utils/constants";
import { navigateToTatvaPractice } from "../../../utils/helper";
import { trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";

export default function NavBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const quizAllowed = localStorage.getItem("quiz_allowed") ?? 0 ;

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    /** Taxonomy tab names for the ZymedPedia bottom bar, keyed by route. */
    const NAV_TAB_NAME: Record<string, string> = {
        "/": "Home",
        "/tatvapedia": "Discover",
        "/tatvapedia/search": "Search",
        "/tatvapedia/library": "Library",
        "/tatvapedia/quiz-list": "Quiz",
    };

    const trackNav = (path: string) =>
        trackZymedEvent(ZYMED_EVENTS.PEDIA_BOTTOM_NAV_CLICKED, {
            tab_name: NAV_TAB_NAME[path],
        });

    const handleNavigation = (path: string) => {
        if (location.pathname === path) {
            return;
        }
        trackNav(path);
        navigate(path);
    };

    const handleHomeNavigation = () => {
        trackNav("/");
        const fromPractice = getLocalStorage(FROM_TATAV_PRACTICE);
        if (fromPractice && fromPractice === "true") {
            navigateToTatvaPractice();
        } else {
            navigate("/");
        }
    };

    // No max-width on the bar below: the app shell (.App) already caps and centres the
    // column, and it is the containing block for position:fixed children, so w-full
    // resolves to the column width. A hardcoded max-w-[450px] left this bar short of the
    // column edge and offset to the left on tablets.
    return (
        <div
            className="flex justify-between px-6 py-2 h-[64px] w-full text-xs fixed bottom-0 left-0 right-0 z-10 bg-white shadow-2xl">
            <div
                className={`flex flex-col justify-center items-center pr-4 border-r border-black border-opacity-10 cursor-pointer ${
                    isActive("/") ? "text-indigo-600" : "text-zinc-700"
                }`}
                role="button"
                aria-label="Go to Home"
                onClick={handleHomeNavigation}
            >
                {isActive("/") ? (
                    <IoArrowBack className="w-6 h-6"/>
                ) : (
                    <IoArrowBackCircleOutline className="w-6 h-6"/>
                )}
                <div className={`mt-1 ${isActive("/") ? "font-semibold" : ""}`}>
                    Home
                </div>
            </div>

            <div
                className={`flex flex-col justify-center items-center cursor-pointer ${
                    isActive("/tatvapedia") ? "text-indigo-600" : "text-zinc-700"
                }`}
                role="button"
                aria-label="Go to Discover"
                onClick={() => handleNavigation("/tatvapedia")}
            >
                {isActive("/tatvapedia") ? (
                    <IoCompass className="w-6 h-6"/>
                ) : (
                    <IoCompassOutline className="w-6 h-6"/>
                )}
                <div
                    className={`mt-1 ${isActive("/tatvapedia") ? "font-semibold" : ""}`}
                >
                    Discover
                </div>
            </div>

            <div
                className={`flex flex-col justify-center items-center cursor-pointer ${
                    isActive("/tatvapedia/search") ? "text-indigo-600" : "text-zinc-700"
                }`}
                role="button"
                aria-label="Go to Search"
                onClick={() => handleNavigation("/tatvapedia/search")}
            >
                {isActive("/tatvapedia/search") ? (
                    <RiSearchFill className="w-6 h-6"/>
                ) : (
                    <RiSearchLine className="w-6 h-6"/>
                )}
                <div
                    className={`mt-1 ${
                        isActive("/tatvapedia/search") ? "font-semibold" : ""
                    }`}
                >
                    Search
                </div>
            </div>

            <div
                className={`flex flex-col justify-center items-center cursor-pointer ${
                    isActive("/tatvapedia/library") ? "text-indigo-600" : "text-zinc-700"
                }`}
                role="button"
                aria-label="Go to Library"
                onClick={() => handleNavigation("/tatvapedia/library")}
            >
                {isActive("/tatvapedia/library") ? (
                    <IoBookmarks className="w-6 h-6"/>
                ) : (
                    <IoBookmarksOutline className="w-6 h-6"/>
                )}
                <div
                    className={`mt-1 ${
                        isActive("/tatvapedia/library") ? "font-semibold" : ""
                    }`}
                >
                    Library
                </div>
            </div>
            {quizAllowed === "1" && (
                <div
                    className={`flex flex-col justify-center items-center cursor-pointer ${
                        isActive("/tatvapedia/quiz-list") ? "text-indigo-600" : "text-zinc-700"
                }`}
                role="button"
                aria-label="Go to Quiz"
                onClick={() => handleNavigation("/tatvapedia/quiz-list")}
            >
                {isActive("/tatvapedia/quiz-list") ? (
                    <IoExtensionPuzzleSharp className="w-6 h-6"/>
                ) : (
                    <IoExtensionPuzzleOutline className="w-6 h-6"/>
                )}
                <div
                    className={`mt-1 ${
                        isActive("/tatvapedia/quiz-list") ? "font-semibold" : ""
                    }`}
                >
                    Quiz
                    </div>
                </div>
            )}
        </div>
    );
}
